import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Calendar, Plus, User, Phone, Mail, Building, 
  Clock, CheckCircle, Star, Send, MessageSquare,
  Video, Users, ThumbsUp, ThumbsDown, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INTERVIEW_TYPES = [
  { value: 'video', label: 'Video Call', icon: Video },
  { value: 'in-person', label: 'In-Person', icon: Users },
  { value: 'phone', label: 'Phone Call', icon: Phone }
];

const STATUS_COLORS = {
  scheduled: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
  no_show: 'bg-gray-100 text-gray-700'
};

// Interview Card Component
function InterviewCard({ interview, onAddFeedback, onSubmitToHR }) {
  const hasRecommendation = interview.feedback?.overall_recommendation;
  
  return (
    <Card className="hover:border-primary/30 transition-colors" data-testid={`interview-${interview.id}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold">{interview.candidate_name}</h3>
              <p className="text-sm text-muted-foreground">{interview.position}</p>
            </div>
          </div>
          <Badge className={STATUS_COLORS[interview.status]}>
            {interview.status.replace('_', ' ').toUpperCase()}
          </Badge>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="flex items-center gap-2 text-sm">
            <Building className="w-4 h-4 text-muted-foreground" />
            <span>{interview.client_company}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Mail className="w-4 h-4 text-muted-foreground" />
            <span>{interview.candidate_email}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <span>{interview.interview_date}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <span>{interview.interview_time}</span>
          </div>
        </div>

        {/* Feedback Summary */}
        {interview.feedback && (
          <div className="bg-muted/50 p-4 rounded-lg mb-4">
            <h4 className="font-medium mb-2 flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Interview Feedback
            </h4>
            <div className="grid grid-cols-3 gap-4 mb-3">
              <div>
                <p className="text-xs text-muted-foreground">Technical</p>
                <div className="flex items-center gap-1">
                  {[...Array(10)].map((_, i) => (
                    <Star 
                      key={i} 
                      className={`w-3 h-3 ${i < interview.feedback.technical_score ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`}
                    />
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Communication</p>
                <div className="flex items-center gap-1">
                  {[...Array(10)].map((_, i) => (
                    <Star 
                      key={i} 
                      className={`w-3 h-3 ${i < interview.feedback.communication_score ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`}
                    />
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Culture Fit</p>
                <div className="flex items-center gap-1">
                  {[...Array(10)].map((_, i) => (
                    <Star 
                      key={i} 
                      className={`w-3 h-3 ${i < interview.feedback.cultural_fit_score ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Recommendation:</span>
              <Badge className={
                interview.feedback.overall_recommendation === 'hire' 
                  ? 'bg-green-100 text-green-700' 
                  : interview.feedback.overall_recommendation === 'reject'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-yellow-100 text-yellow-700'
              }>
                {interview.feedback.overall_recommendation === 'hire' && <ThumbsUp className="w-3 h-3 mr-1" />}
                {interview.feedback.overall_recommendation === 'reject' && <ThumbsDown className="w-3 h-3 mr-1" />}
                {interview.feedback.overall_recommendation.toUpperCase()}
              </Badge>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 pt-4 border-t">
          {interview.status === 'scheduled' && (
            <Button variant="outline" size="sm" onClick={() => onAddFeedback(interview)}>
              <MessageSquare className="w-4 h-4 mr-1" />
              Add Feedback
            </Button>
          )}
          {interview.status === 'completed' && interview.feedback?.overall_recommendation === 'hire' && (
            <Button size="sm" onClick={() => onSubmitToHR(interview)}>
              <Send className="w-4 h-4 mr-1" />
              Submit to HR
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function InterviewsPage() {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);

  // Schedule form state
  const [scheduleForm, setScheduleForm] = useState({
    candidate_name: '',
    candidate_email: '',
    candidate_phone: '',
    position: '',
    client_company: '',
    interview_date: '',
    interview_time: '',
    interview_type: 'video',
    interviewer_name: '',
    notes: ''
  });

  // Feedback form state
  const [feedbackForm, setFeedbackForm] = useState({
    technical_score: 7,
    communication_score: 7,
    cultural_fit_score: 7,
    overall_recommendation: 'hire',
    strengths: '',
    weaknesses: '',
    additional_notes: ''
  });

  // Submit to HR form state
  const [submitForm, setSubmitForm] = useState({
    position: '',
    client_company: '',
    department: '',
    proposed_salary: '',
    currency: 'MYR',
    joining_date: '',
    contract_duration: 'Permanent',
    benefits: '',
    special_conditions: ''
  });

  useEffect(() => {
    fetchInterviews();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchInterviews = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/onboarding/interviews`, {
        headers: getAuthHeaders()
      });
      setInterviews(response.data);
    } catch (error) {
      console.error('Error fetching interviews:', error);
      toast.error('Failed to load interviews');
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleInterview = async () => {
    try {
      await axios.post(`${API_URL}/api/onboarding/interviews/schedule`, scheduleForm, {
        headers: getAuthHeaders()
      });
      toast.success('Interview scheduled successfully');
      fetchInterviews();
      setShowScheduleForm(false);
      setScheduleForm({
        candidate_name: '',
        candidate_email: '',
        candidate_phone: '',
        position: '',
        client_company: '',
        interview_date: '',
        interview_time: '',
        interview_type: 'video',
        interviewer_name: '',
        notes: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule interview');
    }
  };

  const handleSubmitFeedback = async () => {
    try {
      await axios.post(
        `${API_URL}/api/onboarding/interviews/${selectedInterview.id}/feedback`,
        feedbackForm,
        { headers: getAuthHeaders() }
      );
      toast.success('Feedback submitted successfully');
      fetchInterviews();
      setShowFeedbackForm(false);
      setSelectedInterview(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit feedback');
    }
  };

  const handleSubmitToHR = async () => {
    try {
      await axios.post(
        `${API_URL}/api/onboarding/requests/submit?interview_id=${selectedInterview.id}`,
        submitForm,
        { headers: getAuthHeaders() }
      );
      toast.success('Candidate submitted to HR successfully');
      fetchInterviews();
      setShowSubmitForm(false);
      setSelectedInterview(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit to HR');
    }
  };

  const openFeedbackForm = (interview) => {
    setSelectedInterview(interview);
    setShowFeedbackForm(true);
  };

  const openSubmitForm = (interview) => {
    setSelectedInterview(interview);
    setSubmitForm({
      ...submitForm,
      position: interview.position,
      client_company: interview.client_company
    });
    setShowSubmitForm(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="interviews-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Interviews</h1>
          <p className="text-muted-foreground">Schedule and manage candidate interviews</p>
        </div>
        <Button onClick={() => setShowScheduleForm(true)} data-testid="schedule-interview-btn">
          <Plus className="w-4 h-4 mr-2" />
          Schedule Interview
        </Button>
      </div>

      {/* Schedule Interview Modal */}
      {showScheduleForm && (
        <Card className="border-primary/50">
          <CardHeader>
            <CardTitle>Schedule New Interview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Candidate Name *</Label>
                <Input
                  value={scheduleForm.candidate_name}
                  onChange={(e) => setScheduleForm({...scheduleForm, candidate_name: e.target.value})}
                  placeholder="Full name"
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={scheduleForm.candidate_email}
                  onChange={(e) => setScheduleForm({...scheduleForm, candidate_email: e.target.value})}
                  placeholder="candidate@email.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={scheduleForm.candidate_phone}
                  onChange={(e) => setScheduleForm({...scheduleForm, candidate_phone: e.target.value})}
                  placeholder="+60 12-345 6789"
                />
              </div>
              <div className="space-y-2">
                <Label>Position *</Label>
                <Input
                  value={scheduleForm.position}
                  onChange={(e) => setScheduleForm({...scheduleForm, position: e.target.value})}
                  placeholder="Software Engineer"
                />
              </div>
              <div className="space-y-2">
                <Label>Client Company *</Label>
                <Input
                  value={scheduleForm.client_company}
                  onChange={(e) => setScheduleForm({...scheduleForm, client_company: e.target.value})}
                  placeholder="Company name"
                />
              </div>
              <div className="space-y-2">
                <Label>Interviewer Name *</Label>
                <Input
                  value={scheduleForm.interviewer_name}
                  onChange={(e) => setScheduleForm({...scheduleForm, interviewer_name: e.target.value})}
                  placeholder="Interviewer name"
                />
              </div>
              <div className="space-y-2">
                <Label>Interview Date *</Label>
                <Input
                  type="date"
                  value={scheduleForm.interview_date}
                  onChange={(e) => setScheduleForm({...scheduleForm, interview_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Interview Time *</Label>
                <Input
                  type="time"
                  value={scheduleForm.interview_time}
                  onChange={(e) => setScheduleForm({...scheduleForm, interview_time: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Interview Type</Label>
              <div className="flex gap-2">
                {INTERVIEW_TYPES.map((type) => (
                  <Button
                    key={type.value}
                    variant={scheduleForm.interview_type === type.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setScheduleForm({...scheduleForm, interview_type: type.value})}
                  >
                    <type.icon className="w-4 h-4 mr-1" />
                    {type.label}
                  </Button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={scheduleForm.notes}
                onChange={(e) => setScheduleForm({...scheduleForm, notes: e.target.value})}
                placeholder="Additional notes..."
              />
            </div>
            <div className="flex gap-2 pt-4">
              <Button onClick={handleScheduleInterview}>Schedule Interview</Button>
              <Button variant="outline" onClick={() => setShowScheduleForm(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Feedback Modal */}
      {showFeedbackForm && selectedInterview && (
        <Card className="border-primary/50">
          <CardHeader>
            <CardTitle>Interview Feedback - {selectedInterview.candidate_name}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Technical Skills (1-10)</Label>
                <Input
                  type="number"
                  min={1}
                  max={10}
                  value={feedbackForm.technical_score}
                  onChange={(e) => setFeedbackForm({...feedbackForm, technical_score: parseInt(e.target.value)})}
                />
              </div>
              <div className="space-y-2">
                <Label>Communication (1-10)</Label>
                <Input
                  type="number"
                  min={1}
                  max={10}
                  value={feedbackForm.communication_score}
                  onChange={(e) => setFeedbackForm({...feedbackForm, communication_score: parseInt(e.target.value)})}
                />
              </div>
              <div className="space-y-2">
                <Label>Cultural Fit (1-10)</Label>
                <Input
                  type="number"
                  min={1}
                  max={10}
                  value={feedbackForm.cultural_fit_score}
                  onChange={(e) => setFeedbackForm({...feedbackForm, cultural_fit_score: parseInt(e.target.value)})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Recommendation</Label>
              <div className="flex gap-2">
                {['hire', 'maybe', 'reject'].map((rec) => (
                  <Button
                    key={rec}
                    variant={feedbackForm.overall_recommendation === rec ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFeedbackForm({...feedbackForm, overall_recommendation: rec})}
                    className={
                      feedbackForm.overall_recommendation === rec
                        ? rec === 'hire' ? 'bg-green-600' : rec === 'reject' ? 'bg-red-600' : ''
                        : ''
                    }
                  >
                    {rec === 'hire' && <ThumbsUp className="w-4 h-4 mr-1" />}
                    {rec === 'reject' && <ThumbsDown className="w-4 h-4 mr-1" />}
                    {rec === 'maybe' && <AlertCircle className="w-4 h-4 mr-1" />}
                    {rec.toUpperCase()}
                  </Button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Strengths *</Label>
              <Textarea
                value={feedbackForm.strengths}
                onChange={(e) => setFeedbackForm({...feedbackForm, strengths: e.target.value})}
                placeholder="Key strengths observed..."
              />
            </div>
            <div className="space-y-2">
              <Label>Weaknesses *</Label>
              <Textarea
                value={feedbackForm.weaknesses}
                onChange={(e) => setFeedbackForm({...feedbackForm, weaknesses: e.target.value})}
                placeholder="Areas for improvement..."
              />
            </div>
            <div className="space-y-2">
              <Label>Additional Notes</Label>
              <Textarea
                value={feedbackForm.additional_notes}
                onChange={(e) => setFeedbackForm({...feedbackForm, additional_notes: e.target.value})}
                placeholder="Any other observations..."
              />
            </div>
            <div className="flex gap-2 pt-4">
              <Button onClick={handleSubmitFeedback}>Submit Feedback</Button>
              <Button variant="outline" onClick={() => {setShowFeedbackForm(false); setSelectedInterview(null);}}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Submit to HR Modal */}
      {showSubmitForm && selectedInterview && (
        <Card className="border-primary/50">
          <CardHeader>
            <CardTitle>Submit to HR - {selectedInterview.candidate_name}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Position</Label>
                <Input value={submitForm.position} onChange={(e) => setSubmitForm({...submitForm, position: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Client Company</Label>
                <Input value={submitForm.client_company} onChange={(e) => setSubmitForm({...submitForm, client_company: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Department *</Label>
                <Input value={submitForm.department} onChange={(e) => setSubmitForm({...submitForm, department: e.target.value})} placeholder="Engineering, Sales, etc." />
              </div>
              <div className="space-y-2">
                <Label>Contract Duration</Label>
                <Input value={submitForm.contract_duration} onChange={(e) => setSubmitForm({...submitForm, contract_duration: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Proposed Salary *</Label>
                <div className="flex gap-2">
                  <select 
                    className="px-3 py-2 border rounded-md"
                    value={submitForm.currency}
                    onChange={(e) => setSubmitForm({...submitForm, currency: e.target.value})}
                  >
                    <option value="MYR">MYR</option>
                    <option value="USD">USD</option>
                    <option value="SGD">SGD</option>
                  </select>
                  <Input
                    type="number"
                    value={submitForm.proposed_salary}
                    onChange={(e) => setSubmitForm({...submitForm, proposed_salary: e.target.value})}
                    placeholder="Monthly salary"
                    className="flex-1"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Joining Date *</Label>
                <Input
                  type="date"
                  value={submitForm.joining_date}
                  onChange={(e) => setSubmitForm({...submitForm, joining_date: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Benefits</Label>
              <Textarea
                value={submitForm.benefits}
                onChange={(e) => setSubmitForm({...submitForm, benefits: e.target.value})}
                placeholder="Medical, dental, annual leave, etc."
              />
            </div>
            <div className="space-y-2">
              <Label>Special Conditions</Label>
              <Textarea
                value={submitForm.special_conditions}
                onChange={(e) => setSubmitForm({...submitForm, special_conditions: e.target.value})}
                placeholder="Any special terms..."
              />
            </div>
            <div className="flex gap-2 pt-4">
              <Button onClick={handleSubmitToHR}>
                <Send className="w-4 h-4 mr-2" />
                Submit to HR
              </Button>
              <Button variant="outline" onClick={() => {setShowSubmitForm(false); setSelectedInterview(null);}}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Interviews List */}
      <div className="space-y-4">
        {interviews.length === 0 ? (
          <Card className="p-12 text-center">
            <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No interviews scheduled</h3>
            <p className="text-muted-foreground mb-4">Schedule your first interview to start the hiring process</p>
            <Button onClick={() => setShowScheduleForm(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Schedule Interview
            </Button>
          </Card>
        ) : (
          interviews.map((interview) => (
            <InterviewCard
              key={interview.id}
              interview={interview}
              onAddFeedback={openFeedbackForm}
              onSubmitToHR={openSubmitForm}
            />
          ))
        )}
      </div>
    </div>
  );
}
